/**
 * TaskStatusTag Components
 * Tags for displaying task status
 *
 * Four variants for each status:
 * - IconBadge: Small pill with just the icon (for inline use)
 * - IconBadgeWithCount: Pill with icon and count number (for summaries)
 * - Badge: Pill with icon and text label (e.g., "Overdue")
 * - CountBadge: Pill with icon, count, and label (e.g., "2 Overdue")
 */

import { Box, HStack, Icon, Text, Tooltip } from "@chakra-ui/react";
import {
  LuFlame,
  LuClock,
  LuCircleCheck,
  LuCircleSlash,
  LuCircleAlert,
  LuCalendarClock,
  LuCircleDot,
  LuCircleX,
  LuHistory,
} from "react-icons/lu";
import { BadgeSize, BADGE_SIZE_STYLES, COUNT_BADGE_SIZE_STYLES } from "design-system/foundation/BadgeStyles";

// Status configuration
const STATUS_CONFIG = {
  overdue: {
    icon: LuClock,
    label: "Overdue",
    bg: "red.50",
    color: "red.500",
  },
  urgent: {
    icon: LuFlame,
    label: "Urgent",
    bg: "orange.50",
    color: "orange.500",
  },
  completed: {
    icon: LuCircleCheck,
    label: "Completed",
    bg: "green.50",
    color: "green.500",
  },
  skipped: {
    icon: LuCircleSlash,
    label: "Skipped",
    bg: "gray.100",
    color: "gray.500",
  },
  closedStale: {
    icon: LuHistory,
    label: "Closed (stale)",
    bg: "gray.100",
    color: "gray.500",
  },
  blocked: {
    icon: LuCircleAlert,
    label: "Blocked",
    bg: "pink.100",
    color: "pink.600",
  },
  notStarted: {
    icon: LuCalendarClock,
    label: "Not Started",
    bg: "blue.50",
    color: "blue.500",
  },
  active: {
    icon: LuCircleDot,
    label: "Active",
    bg: "blue.50",
    color: "blue.500",
  },
  issue: {
    icon: LuCircleX,
    label: "Issue",
    bg: "red.50",
    color: "red.500",
  },
  checkpointBehind: {
    icon: LuCircleAlert,
    label: "Checkpoint",
    bg: "pink.100",
    color: "pink.600",
  },
} as const;

export type StatusType = keyof typeof STATUS_CONFIG;

// Base components

interface StatusIconBadgeProps {
  status: StatusType;
  tooltipLabel?: string;
}

interface StatusBadgeProps {
  status: StatusType;
  size?: BadgeSize;
}

function StatusIconBadge({ status, tooltipLabel }: StatusIconBadgeProps) {
  const config = STATUS_CONFIG[status];
  return (
    <Tooltip label={tooltipLabel || config.label} placement="top" hasArrow>
      <Box as="span" bg={config.bg} p={1} borderRadius="full" display="inline-flex">
        <Icon as={config.icon} color={config.color} boxSize={3} />
      </Box>
    </Tooltip>
  );
}

interface StatusIconBadgeWithCountProps {
  status: StatusType;
  count: number;
}

function StatusIconBadgeWithCount({ status, count }: StatusIconBadgeWithCountProps) {
  if (count <= 0) return null;

  const config = STATUS_CONFIG[status];
  return (
    <Tooltip label={`${config.label} Tasks`} placement="top">
      <HStack spacing={1} bg={config.bg} color={config.color} px={2} py={0.5} borderRadius="full">
        <Icon as={config.icon} boxSize={3.5} />
        <Text fontSize="sm" fontWeight="medium">
          {count}
        </Text>
      </HStack>
    </Tooltip>
  );
}

function StatusBadge({ status, size = "md" }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status];
  const styles = BADGE_SIZE_STYLES[size];

  return (
    <HStack
      spacing={1}
      bg={config.bg}
      color={config.color}
      px={styles.px}
      py={styles.py}
      borderRadius="md"
      display="inline-flex"
    >
      <Icon as={config.icon} boxSize={styles.iconSize} />
      <Text fontSize={styles.fontSize} fontWeight="medium">
        {config.label}
      </Text>
    </HStack>
  );
}

interface StatusCountBadgeProps {
  status: StatusType;
  count: number;
  size?: BadgeSize;
}

function StatusCountBadge({ status, count, size = "md" }: StatusCountBadgeProps) {
  if (count <= 0) return null;

  const config = STATUS_CONFIG[status];
  const styles = COUNT_BADGE_SIZE_STYLES[size];

  return (
    <HStack spacing={1} bg={config.bg} color={config.color} px={styles.px} py={styles.py} borderRadius="full">
      <Icon as={config.icon} boxSize={styles.iconSize} />
      <Text fontSize={styles.fontSize} fontWeight="medium">
        {count} {config.label}
        {count !== 1 ? "s" : ""}
      </Text>
    </HStack>
  );
}

// Overdue
function OverdueIconBadge() {
  return <StatusIconBadge status="overdue" />;
}

function OverdueIconBadgeWithCount({ count }: { count: number }) {
  return <StatusIconBadgeWithCount status="overdue" count={count} />;
}

function OverdueBadge({ size }: { size?: BadgeSize }) {
  return <StatusBadge status="overdue" size={size} />;
}

function OverdueCountBadge({ count, size }: { count: number; size?: BadgeSize }) {
  return <StatusCountBadge status="overdue" count={count} size={size} />;
}

// Urgent
function UrgentIconBadge() {
  return <StatusIconBadge status="urgent" />;
}

function UrgentIconBadgeWithCount({ count }: { count: number }) {
  return <StatusIconBadgeWithCount status="urgent" count={count} />;
}

function UrgentBadge({ size }: { size?: BadgeSize }) {
  return <StatusBadge status="urgent" size={size} />;
}

function UrgentCountBadge({ count, size }: { count: number; size?: BadgeSize }) {
  return <StatusCountBadge status="urgent" count={count} size={size} />;
}

// Completed
function CompletedIconBadge() {
  return <StatusIconBadge status="completed" />;
}

function CompletedIconBadgeWithCount({ count }: { count: number }) {
  return <StatusIconBadgeWithCount status="completed" count={count} />;
}

function CompletedBadge({ size }: { size?: BadgeSize }) {
  return <StatusBadge status="completed" size={size} />;
}

function CompletedCountBadge({ count, size }: { count: number; size?: BadgeSize }) {
  return <StatusCountBadge status="completed" count={count} size={size} />;
}

// Skipped
function SkippedIconBadge() {
  return <StatusIconBadge status="skipped" />;
}

function SkippedIconBadgeWithCount({ count }: { count: number }) {
  return <StatusIconBadgeWithCount status="skipped" count={count} />;
}

function SkippedBadge({ size }: { size?: BadgeSize }) {
  return <StatusBadge status="skipped" size={size} />;
}

function SkippedCountBadge({ count, size }: { count: number; size?: BadgeSize }) {
  return <StatusCountBadge status="skipped" count={count} size={size} />;
}

// Closed (Stale)
function ClosedStaleIconBadge() {
  return <StatusIconBadge status="closedStale" />;
}

function ClosedStaleIconBadgeWithCount({ count }: { count: number }) {
  return <StatusIconBadgeWithCount status="closedStale" count={count} />;
}

function ClosedStaleBadge({ size }: { size?: BadgeSize }) {
  return <StatusBadge status="closedStale" size={size} />;
}

function ClosedStaleCountBadge({ count, size }: { count: number; size?: BadgeSize }) {
  return <StatusCountBadge status="closedStale" count={count} size={size} />;
}

// Blocked
function BlockedIconBadge() {
  return <StatusIconBadge status="blocked" />;
}

function BlockedIconBadgeWithCount({ count }: { count: number }) {
  return <StatusIconBadgeWithCount status="blocked" count={count} />;
}

function BlockedBadge({ size }: { size?: BadgeSize }) {
  return <StatusBadge status="blocked" size={size} />;
}

function BlockedCountBadge({ count, size }: { count: number; size?: BadgeSize }) {
  return <StatusCountBadge status="blocked" count={count} size={size} />;
}

// Not Started
function NotStartedIconBadge() {
  return <StatusIconBadge status="notStarted" />;
}

function NotStartedIconBadgeWithCount({ count }: { count: number }) {
  return <StatusIconBadgeWithCount status="notStarted" count={count} />;
}

function NotStartedBadge({ size }: { size?: BadgeSize }) {
  return <StatusBadge status="notStarted" size={size} />;
}

function NotStartedCountBadge({ count, size }: { count: number; size?: BadgeSize }) {
  return <StatusCountBadge status="notStarted" count={count} size={size} />;
}

// Active
function ActiveIconBadge() {
  return <StatusIconBadge status="active" />;
}

function ActiveIconBadgeWithCount({ count }: { count: number }) {
  return <StatusIconBadgeWithCount status="active" count={count} />;
}

function ActiveBadge({ size }: { size?: BadgeSize }) {
  return <StatusBadge status="active" size={size} />;
}

function ActiveCountBadge({ count, size }: { count: number; size?: BadgeSize }) {
  return <StatusCountBadge status="active" count={count} size={size} />;
}

// Issue
function IssueIconBadge() {
  return <StatusIconBadge status="issue" />;
}

function IssueIconBadgeWithCount({ count }: { count: number }) {
  return <StatusIconBadgeWithCount status="issue" count={count} />;
}

function IssueBadge({ size }: { size?: BadgeSize }) {
  return <StatusBadge status="issue" size={size} />;
}

function IssueCountBadge({ count, size }: { count: number; size?: BadgeSize }) {
  return <StatusCountBadge status="issue" count={count} size={size} />;
}

// Checkpoint Behind
function CheckpointBehindIconBadge() {
  return <StatusIconBadge status="checkpointBehind" />;
}

function CheckpointBehindIconBadgeWithCount({ count }: { count: number }) {
  return <StatusIconBadgeWithCount status="checkpointBehind" count={count} />;
}

function CheckpointBehindBadge({ size }: { size?: BadgeSize }) {
  return <StatusBadge status="checkpointBehind" size={size} />;
}

function CheckpointBehindCountBadge({ count, size }: { count: number; size?: BadgeSize }) {
  return <StatusCountBadge status="checkpointBehind" count={count} size={size} />;
}

// Dynamic status icon badge component (for use when status is determined at runtime)
interface IconBadgeProps {
  status: StatusType;
  size?: BadgeSize;
  tooltipLabel?: string;
}

function IconBadge({ status, size = "md", tooltipLabel }: IconBadgeProps) {
  const config = STATUS_CONFIG[status];
  if (!config) return null;

  const styles = BADGE_SIZE_STYLES[size];
  const label = tooltipLabel || config.label;

  return (
    <Tooltip label={label} placement="top" hasArrow>
      <Box as="span" display="inline-flex">
        <Icon
          as={config.icon}
          color={config.color}
          boxSize={styles.iconSize}
          flexShrink={0}
          title={label}
          cursor="default"
        />
      </Box>
    </Tooltip>
  );
}

// Open Tasks (special component)
interface OpenTasksProps {
  count: number;
  totalCount?: number;
}

function OpenTasksComponent({ count, totalCount }: OpenTasksProps) {
  if (totalCount === 0) {
    return (
      <Text fontSize="sm" color="gray.400">
        No Tasks
      </Text>
    );
  }

  return (
    <Text fontSize="sm" color="gray.600">
      {count} open
    </Text>
  );
}

// Public API
export const TaskStatusTag = {
  // Overdue
  OverdueIconBadge,
  OverdueIconBadgeWithCount,
  OverdueBadge,
  OverdueCountBadge,
  // Urgent
  UrgentIconBadge,
  UrgentIconBadgeWithCount,
  UrgentBadge,
  UrgentCountBadge,
  // Completed
  CompletedIconBadge,
  CompletedIconBadgeWithCount,
  CompletedBadge,
  CompletedCountBadge,
  // Skipped
  SkippedIconBadge,
  SkippedIconBadgeWithCount,
  SkippedBadge,
  SkippedCountBadge,
  // Closed (Stale)
  ClosedStaleIconBadge,
  ClosedStaleIconBadgeWithCount,
  ClosedStaleBadge,
  ClosedStaleCountBadge,
  // Blocked
  BlockedIconBadge,
  BlockedIconBadgeWithCount,
  BlockedBadge,
  BlockedCountBadge,
  // Not Started
  NotStartedIconBadge,
  NotStartedIconBadgeWithCount,
  NotStartedBadge,
  NotStartedCountBadge,
  // Active
  ActiveIconBadge,
  ActiveIconBadgeWithCount,
  ActiveBadge,
  ActiveCountBadge,
  // Issue
  IssueIconBadge,
  IssueIconBadgeWithCount,
  IssueBadge,
  IssueCountBadge,
  // Checkpoint Behind
  CheckpointBehindIconBadge,
  CheckpointBehindIconBadgeWithCount,
  CheckpointBehindBadge,
  CheckpointBehindCountBadge,
  // Other
  OpenTasks: OpenTasksComponent,
  // Dynamic icon badge for runtime status determination
  IconBadge,
};
